import { IbssPage } from '../../../../Components/Core/BasePage/IbssPage';
import { appContext } from '../../../../AppContext';
import { ApiHelper, PagedResponse } from "@iconics/core";
import { IOntologyNodeData, Ontology } from './DataModels';
import { IStyling } from '../../../../Common/GraphStyleHelper';
import EditOntologyNodeDialog from './EditOntologyNodeDialog';
import { JSX } from 'react';
import { IRouterProps, ITreeDataGridColumn, withRouting } from "@iconics/components";
import { SerendieSymbol } from '@serendie/symbols';
import { MultilingualTextField } from "@iconics/components";
import { ILanguageValues, LanguageValuesHelper } from "@iconics/localisation";
import { Switch } from '@serendie/ui';
import { Validator } from '@iconics/core';
import { TypeHelper } from '@iconics/core';
import { Button, ButtonBar, Card, ColumnWidthUnits, DeleteButton, GridContainer, GridItem, IPageNavSection, LayoutContainer, LayoutContent, LayoutFooter, LayoutRight, LeftButtons, PageNav, RightButtons, TextField, TickSymbol, TreeDataGrid } from '@iconics/components';
import { ApiConstants } from "@iconics/core";

class EditOntology extends IbssPage<IProps, IState>
{
    private validator = new Validator(appContext().core.validationOptions);

    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }
    private languageValuesHelper: LanguageValuesHelper;

    private readonly ontologyNodeColumns: Array<ITreeDataGridColumn<IOntologyNode>> = [
        {
            title: this.labels.OntologyNode_Name,
            getCellValue: i => i.name,
            getDisplayName: i => i.name,
            columnWidth: 40,
            columnWidthUnits: ColumnWidthUnits.Percents,
        },
        {
            title: this.labels.OntologyNode_IsEnabled,
            getCellValue: i => i.isEnabled,
            template: i => <TickSymbol value={i.value as boolean} />,
            columnWidth: 20,
            columnWidthUnits: ColumnWidthUnits.Percents,
        },
        {
            title: this.labels.OntologyNode_IsRecursive,
            getCellValue: i => i.isRecursive,
            template: i => <TickSymbol value={i.value as boolean} />,
            columnWidth: 20,
            columnWidthUnits: ColumnWidthUnits.Percents,
        },
        {
            title: this.labels.OntologyNode_IsRequired,
            getCellValue: i => i.isRequired,
            template: i => <TickSymbol value={i.value as boolean} />,
            columnWidth: 20,
            columnWidthUnits: ColumnWidthUnits.Percents,
        },
    ];

    private userCanCreate: boolean;
    private userCanUpdate: boolean;
    private userCanDelete: boolean;
    private userCanEnable: boolean;
    private userCanDisable: boolean;

    constructor(props: IProps)
    {
        super(props);
        this.languageValuesHelper = new LanguageValuesHelper(appContext().localisation);

        this.state =
        {
            id: '',
            name: '',
            displayName: this.languageValuesHelper.default(),
            description: '',
            isEnabled: false,
            concurrencyToken: '',
            nameError: '',
            descriptionError: '',
            ontologyNodes: [],
            selectedOntologyNodeIds: [],
            showEditNodeDialog: false,
            selectedOntologyNode: null,
            sections: [],
        };

        this.userCanCreate = this.local.hasRight('DATAMODEL.Ontologies.Create');
        this.userCanUpdate = this.local.hasRight('DATAMODEL.Ontologies.Update');
        this.userCanDelete = this.local.hasRight('DATAMODEL.Ontologies.Delete');
        this.userCanEnable = this.local.hasRight('API.Ontologies.Enable');
        this.userCanDisable = this.local.hasRight('API.Ontologies.Disable');
    }

    private get isNew(): boolean
    {
        return (!this.props.params.id);
    }

    public async componentDidMount(): Promise<void>
    {
        this.pageTitle = this.labels.Platform_Ontology_PageTitle;
        this.backUrl = '/admin/ontologies';
        this.validator
            .addInput('name', () => this.state.name, i => this.setState({ nameError: i }))
            .isRequired();
        this.validator
            .addInput('description', () => this.state.description, i => this.setState({ descriptionError: i }))
            .isRequired();
        if (!this.isNew)
        {
            await this.loadOntology();
            await this.loadRootNodes();
        }
    }

    private async loadOntology(): Promise<void>
    {
        await this.loading(async () =>
        {
            const ontologyData = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.get<Ontology>({
                id: this.props.params.id ?? '',
                tenancy: appContext().tenantId,
                select: Ontology
            });

            await this.setStateAsync({
                id: ontologyData.Id,
                name: ontologyData.Name,
                displayName: this.languageValuesHelper.fromString(ontologyData.DisplayName),
                description: ontologyData.Description,
                isEnabled: TypeHelper.toBoolean(ontologyData.IsEnabled) ?? false,
                concurrencyToken: ontologyData["@odata.etag"]
            });
        });
    }

    private async loadRootNodes(): Promise<void>
    {
        if (!this.props.params.id)
        {
            await this.setStateAsync({ ontologyNodes: [] });
            return;
        }
        const ontologyNodes = await this.getOntologyNodes();
        await this.setStateAsync({ ontologyNodes });
    }

    private async getOntologyNodes(parentId?: string): Promise<IOntologyNode[]>
    {
        if (!this.props.params.id)
        {
            return [];
        }

        const id = this.props.params.id;

        const responseData = await ApiHelper.getAllPages<IOntologyNodeData>(() =>
            appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.get<PagedResponse<IOntologyNodeData[]>>({
                tenancy: appContext().tenantId,
                id: id,
                filter: `Parent_ID eq ${parentId === undefined ? "null" : `'${parentId}'`}`
            })
        );
        const children = responseData.map(x => this.toOntologyNode(x));

        return children;
    }

    private async getOntologyNodeById(nodeId: string): Promise<IOntologyNode | null>
    {
        if (!this.props.params.id)
        {
            return null;
        }
        const data = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.byOntologynodeid.get<IOntologyNodeData>({
            tenancy: appContext().tenantId,
            id: this.props.params.id,
            ontologynodeid: nodeId,
        });
        return this.toOntologyNode(data);
    }

    private toOntologyNode(x: IOntologyNodeData): IOntologyNode
    {
        return {
            id: x.Id,
            name: x.Name,
            displayName: this.languageValuesHelper.fromString(x.DisplayName),
            isEnabled: TypeHelper.toBoolean(x.IsEnabled) ?? false,
            isRecursive: TypeHelper.toBoolean(x.IsRecursive) ?? false,
            isRequired: TypeHelper.toBoolean(x.IsRequired) ?? false,
            parentId: x.Parent_ID ?? '',
            parentName: this.state.ontologyNodes.find(n => n.id === x.Parent_ID)?.name ?? '',
            styling: x.Styling ?? null,
            etag: x['@odata.etag']
        };
    }

    private async handleLoadChildren(parent: IOntologyNode): Promise<IOntologyNode[]>
    {
        const children = await this.getOntologyNodes(parent.id);
        this.setState({ ontologyNodes: this.state.ontologyNodes.concat(children) });
        return children;
    }

    private async handleDelete(): Promise<void>
    {
        await this.loading(async () =>
        {
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.delete({
                tenancy: appContext().tenantId,
                id: this.state.id,
            });
            this.props.navigate("/admin/ontologies")
        });
    }

    private async handleSave(): Promise<void>
    {
        if (!this.validator.validateAll())
        {
            return;
        }
        await this.loading(async () =>
        {
            if (this.isNew)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.post({
                    tenancy: appContext().tenantId,
                    body: {
                        Name: this.state.name,
                        Description: this.state.description,
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        IsEnabled: this.state.isEnabled ? 'Yes' : 'No',
                    }
                });
            }
            else
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.put<Ontology>({
                    tenancy: appContext().tenantId,
                    id: this.state.id,
                    body: {
                        Name: this.state.name,
                        Description: this.state.description,
                        IsEnabled: this.state.isEnabled ? 'Yes' : 'No',
                        DisplayName: this.languageValuesHelper.toString(this.state.displayName),
                        '@odata.etag': this.state.concurrencyToken,
                    }
                });
            }
            this.props.navigate("/admin/ontologies")
        });
    }

    private async handleSaveOntologyNode(id: string): Promise<void>
    {
        this.setState({ showEditNodeDialog: false, selectedOntologyNode: null });

        await this.loading(async () =>
        {
            const node = await this.getOntologyNodeById(id);
            if (node == null)
            {
                return;
            }

            this.setState(prev => ({
                ontologyNodes: prev.ontologyNodes.some(n => n.id === node.id)
                    ? prev.ontologyNodes.map(n => n.id === node.id ? node : n)
                    : [...prev.ontologyNodes, node],
                selectedOntologyNodeIds: []
            }));
        });
    }

    private async handleEnableOntologyNodes(): Promise<void>
    {
        await this.loading(async () =>
        {
            await this.updateEnabled(true);
        });
    }

    private async handleDisableOntologyNodes(): Promise<void>
    {
        await this.loading(async () =>
        {
            await this.updateEnabled(false);
        });
    }

    private async updateEnabled(enabled: boolean): Promise<void>
    {
        for (const id of this.state.selectedOntologyNodeIds)
        {
            const node = this.state.ontologyNodes.find(x => x.id === id);
            if (node == null || node.isEnabled === enabled)
            {
                continue;
            }
            await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.byOntologynodeid.patch({
                tenancy: appContext().tenantId,
                id: this.state.id,
                ontologynodeid: node.id,
                body: {
                    IsEnabled: enabled ? 'Yes' : 'No',
                    "@odata.etag": node.etag
                }
            });

            const updated = await this.getOntologyNodeById(node.id);
            if (updated != null)
            {
                this.setState(prev => ({ ontologyNodes: prev.ontologyNodes.map(n => n.id === updated.id ? updated : n) }));
            }
        }
    }

    private handleEditOntologyNode(id: string): React.ReactNode
    {
        if (!this.userCanUpdate)
        {
            return;
        }

        const ontologyNode = this.state.ontologyNodes.find(i => i.id === id);
        if (!ontologyNode)
        {
            return;
        }

        this.setState({
            showEditNodeDialog: true,
            selectedOntologyNode: {
                id: ontologyNode.id,
                name: ontologyNode.name,
                displayName: ontologyNode.displayName,
                isEnabled: ontologyNode.isEnabled,
                isRequired: ontologyNode.isRequired,
                isRecursive: ontologyNode.isRecursive,
                parentId: ontologyNode.parentId,
                parentName: ontologyNode.parentName,
                styling: ontologyNode.styling,
                etag: ontologyNode.etag,
            }
        });
    }

    private async handleDeleteOntologyNodes(): Promise<void>
    {
        await this.loading(async () =>
        {
            const selectedIds = this.state.selectedOntologyNodeIds;
            for (const id of selectedIds)
            {
                await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.ontologies.byId.nodes.byOntologynodeid.delete({
                    tenancy: appContext().tenantId,
                    id: this.state.id,
                    ontologynodeid: id,
                });
            }

            this.setState(prev => ({
                selectedOntologyNodeIds: [],
                ontologyNodes: prev.ontologyNodes.filter(n => !selectedIds.includes(n.id)),
            }));
        });
    }

    public render(): JSX.Element
    {
        return (
            <>
                <LayoutContainer>
                    <LayoutContent>
                        <GridContainer spacing="cards">
                            <GridItem>
                                <Card
                                    icon={<SerendieSymbol name='loader' />}
                                    title={this.labels.Platform_Ontology_DetailsTitle}
                                    subTitle={this.labels.Platform_Ontology_DetailsSubTitle}
                                    onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 0) }))}
                                >
                                    <GridContainer>
                                        <GridItem expanded={6}>
                                            <TextField
                                                required
                                                label={this.labels.Ontology_Name}
                                                placeholder={this.labels.Ontology_Name}
                                                value={this.state.name}
                                                onChange={e => this.setState({ name: e.target.value }, () => this.validator.validate('name'))}
                                                invalid={this.state.nameError !== ''}
                                                invalidMessage={this.state.nameError}
                                            />
                                        </GridItem>
                                        <GridItem expanded={6}>
                                            <MultilingualTextField
                                                label={this.labels.Ontology_DisplayName}
                                                placeholder={this.labels.Ontology_DisplayName}
                                                value={this.state.displayName}
                                                onChange={value => this.setState({ displayName: value })}
                                                localisation={appContext().localisation}
                                            />
                                        </GridItem>
                                        <GridItem>
                                            <TextField
                                                required
                                                label={this.labels.Ontology_Description}
                                                placeholder={this.labels.Ontology_Description}
                                                value={this.state.description}
                                                onChange={e => this.setState({ description: e.target.value }, () => this.validator.validate('description'))}
                                                invalid={this.state.descriptionError !== ''}
                                                invalidMessage={this.state.descriptionError}
                                            />
                                        </GridItem>
                                        <GridItem>
                                            <Switch
                                                label={this.labels.Ontology_IsEnabled}
                                                checked={this.state.isEnabled}
                                                onCheckedChange={e => this.setState({ isEnabled: e.checked as boolean })}
                                            />
                                        </GridItem>
                                    </GridContainer>
                                </Card>
                            </GridItem>
                            {
                                !this.isNew &&
                                <GridItem>
                                    <Card
                                        title={this.labels.Platform_Ontology_NodeListTitle}
                                        subTitle={this.labels.Platform_Ontology_NodeListSubTitle}
                                        onSectionUpdate={i => this.setState(prev => ({ sections: PageNav.update(prev.sections, i, 1) }))}
                                    >
                                        <GridContainer>
                                            <GridItem>
                                                <ButtonBar>
                                                    <RightButtons>
                                                        <DeleteButton
                                                            small={true}
                                                            disabled={!this.userCanDelete || this.state.selectedOntologyNodeIds.length < 1}
                                                            onDelete={() => this.handleDeleteOntologyNodes()}
                                                            dialogMessage={this.labels.Platform_OntologyNode_ConfirmDeleteMsg}
                                                        />
                                                        <Button
                                                            size='small'
                                                            styleType='ghost'
                                                            leftIcon={<SerendieSymbol name='close' />}
                                                            onClick={() => this.handleDisableOntologyNodes()}
                                                            disabled={!this.userCanDisable || this.state.selectedOntologyNodeIds.length < 1}
                                                        >
                                                            {this.labels.Platform_CommonAction_Disable}
                                                        </Button>
                                                        <Button
                                                            size='small'
                                                            styleType='ghost'
                                                            leftIcon={<SerendieSymbol name='check' />}
                                                            onClick={() => this.handleEnableOntologyNodes()}
                                                            disabled={!this.userCanEnable || this.state.selectedOntologyNodeIds.length < 1}
                                                        >
                                                            {this.labels.Platform_CommonAction_Enable}
                                                        </Button>
                                                        <Button
                                                            size='small'
                                                            styleType='ghost'
                                                            leftIcon={<SerendieSymbol name='pencil' />}
                                                            onClick={() => this.handleEditOntologyNode(this.state.selectedOntologyNodeIds[0])}
                                                            disabled={!this.userCanUpdate || this.state.selectedOntologyNodeIds.length !== 1}
                                                        >
                                                            {this.labels.Platform_CommonAction_Edit}
                                                        </Button>
                                                        <Button
                                                            size='small'
                                                            styleType='ghost'
                                                            leftIcon={<SerendieSymbol name='plus' />}
                                                            onClick={() => this.setState({ showEditNodeDialog: true, selectedOntologyNode: null })}
                                                            disabled={!this.userCanCreate || this.state.selectedOntologyNodeIds.length > 0}
                                                        >
                                                            {this.labels.Platform_CommonAction_Add}
                                                        </Button>
                                                    </RightButtons>
                                                </ButtonBar>
                                            </GridItem>
                                            <GridItem>
                                                <TreeDataGrid<IOntologyNode>
                                                    data={this.state.ontologyNodes}
                                                    onLoadChildren={(parent, _) => this.handleLoadChildren(parent)}
                                                    getNodeId={i => i.id}
                                                    getParentNodeId={i => i.parentId || null}
                                                    showCheckBoxes={true}
                                                    selectedIds={this.state.selectedOntologyNodeIds}
                                                    onSelectionChange={ids => this.setState({ selectedOntologyNodeIds: ids })}
                                                    columns={this.ontologyNodeColumns}
                                                />
                                            </GridItem>
                                        </GridContainer>
                                    </Card>
                                </GridItem>
                            }
                        </GridContainer>
                    </LayoutContent>
                    <LayoutRight>
                        <PageNav
                            sections={this.state.sections}
                        />
                    </LayoutRight>
                    <LayoutFooter>
                        <ButtonBar>
                            <LeftButtons>
                                {
                                    !this.isNew &&
                                    <DeleteButton
                                        disabled={!this.userCanDelete}
                                        dialogMessage={this.labels.Platform_Ontology_ConfirmDeleteMsg}
                                        onDelete={() => this.handleDelete()}
                                    />
                                }
                            </LeftButtons>
                            <RightButtons>
                                <Button
                                    styleType='outlined'
                                    onClick={() => this.props.navigate("/admin/ontologies")}
                                >
                                    {this.labels.Platform_CommonAction_Cancel
                                    }
                                </Button>
                                <Button
                                    styleType='filled'
                                    disabled={!this.validator.isValid}
                                    onClick={() => this.handleSave()}
                                >
                                    {this.labels.Platform_CommonAction_Save
                                    }
                                </Button>
                            </RightButtons>
                        </ButtonBar>
                    </LayoutFooter>
                </LayoutContainer>
                <EditOntologyNodeDialog
                    open={this.state.showEditNodeDialog}
                    onClose={() => this.setState({ showEditNodeDialog: false, selectedOntologyNode: null })}
                    selectedOntologyNode={this.state.selectedOntologyNode}
                    onSave={(id) => this.handleSaveOntologyNode(id)}
                    tenancy={appContext().tenantId}
                    ontologyId={this.state.id}
                />
            </>
        )
    }
}

export default withRouting(EditOntology);

export interface IProps extends IRouterProps<IQueryParams>
{
}

export interface IState
{
    id: string;
    name: string;
    displayName: ILanguageValues;
    description: string;
    isEnabled: boolean;
    concurrencyToken: string;
    nameError: string;
    descriptionError: string;
    ontologyNodes: IOntologyNode[];
    selectedOntologyNodeIds: string[];
    showEditNodeDialog: boolean;
    selectedOntologyNode: IOntologyNode | null,
    sections: IPageNavSection[];
}

export interface IQueryParams
{
    id: string | undefined;
}

export interface IOntologyNode
{
    id: string;
    name: string;
    displayName: ILanguageValues;
    isEnabled: boolean;
    isRecursive: boolean;
    isRequired: boolean;
    parentId: string;
    parentName: string;
    styling: IStyling | null;
    etag: string;
}
